import { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { Config } from "contexts/CollectionDetails";
import { ReactComponent as ScanQrCode } from "assets/scan-qr-code.svg";
import { Button, ButtonVariant } from "components/Form/Button";
import { Modal } from "components/Modal";
import { QrCameraReader } from "components/QrCameraReader";
import {
  getTaskNavigationSteps,
  getTaskNavigationTask,
  readTaskNavigationQr,
  TaskNavigationTarget,
} from "lib/task-navigation-qr";
import { getResolvedWorkTask } from "lib/work-tasks";

export function TaskNavigationScanner({
  config,
  collectionId,
  disabled = false,
  onSelect,
}: {
  config: Config;
  collectionId: string;
  disabled?: boolean;
  onSelect: (target: TaskNavigationTarget) => void;
}) {
  const [open, setOpen] = useState(false);
  const [reading, setReading] = useState(false);
  const [hasRead, setHasRead] = useState(false);
  const [error, setError] = useState("");
  const [manual, setManual] = useState("");
  const trigger = useRef<HTMLButtonElement>(null);
  const dialog = useRef<HTMLElement>(null);
  const attempt = useRef(0);
  const titleId = useId();
  const enabled =
    config.selectedCollection?.data.enable_task_qr_navigation === true;
  const documentId = config.selectedCollection?.id;
  const cancelPending = useCallback(() => {
    attempt.current++;
  }, []);
  const close = useCallback(() => {
    attempt.current++;
    setOpen(false);
    setReading(false);
    trigger.current?.focus();
  }, []);
  useEffect(() => {
    close();
    return cancelPending;
  }, [close, collectionId, documentId, enabled, disabled, cancelPending]);
  useEffect(() => {
    if (open)
      dialog.current?.querySelector<HTMLButtonElement>("button")?.focus();
  }, [open]);

  const read = useCallback(
    async (raw: string) => {
      const current = ++attempt.current;
      setHasRead(true);
      setReading(true);
      setError("");
      try {
        const target = readTaskNavigationQr(raw, collectionId);
        const task = getTaskNavigationTask(target, config, collectionId);
        if (target.entry_id && task.workTaskDocumentId) {
          const flow = await getResolvedWorkTask(
            task.workTaskDocumentId,
            task.task_id,
            config.selectedCollection
          );
          getTaskNavigationSteps(flow, target.entry_id);
        }
        if (current !== attempt.current) return;
        close();
        onSelect(target);
      } catch (problem) {
        if (current !== attempt.current) return;
        setError(
          problem instanceof Error
            ? problem.message
            : "This station could not be opened. Please try again."
        );
      } finally {
        if (current === attempt.current) setReading(false);
      }
    },
    [close, collectionId, config, onSelect]
  );
  if (!enabled) return null;

  return (
    <>
      <div className="flex items-center gap-4 rounded-xl bg-f4f7ff p-4">
        <div className="flex-1 space-y-1">
          <p className="font-medium">Scan station / task</p>
          <p className="text-sm text-858494">
            Open the right task at your station.
          </p>
        </div>
        <button
          ref={trigger}
          type="button"
          aria-label="Scan station or task"
          disabled={disabled}
          className="flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-xl bg-3461fd text-white disabled:opacity-40"
          onClick={() => {
            setError("");
            setManual("");
            setHasRead(false);
            setOpen(true);
          }}
        >
          <ScanQrCode
            className="h-6 w-6"
            aria-hidden="true"
            focusable="false"
          />
        </button>
      </div>
      {createPortal(
        <Modal show={open} onClose={close}>
          <section
            ref={dialog}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            className="space-y-4"
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                event.stopPropagation();
                close();
              }
              if (event.key === "Tab") {
                const controls = Array.from(
                  dialog.current?.querySelectorAll<HTMLElement>(
                    "button:not(:disabled), textarea, summary"
                  ) || []
                ).filter((item) => item.getClientRects().length > 0);
                const first = controls[0];
                const last = controls[controls.length - 1];
                if (event.shiftKey && document.activeElement === first) {
                  event.preventDefault();
                  last?.focus();
                } else if (!event.shiftKey && document.activeElement === last) {
                  event.preventDefault();
                  first?.focus();
                }
              }
            }}
          >
            <div className="flex items-center justify-between gap-3">
              <h2 id={titleId} className="text-xl font-semibold">
                Scan station / task
              </h2>
              <button type="button" onClick={close} className="p-2 text-3461fd">
                Close
              </button>
            </div>
            <p className="text-sm text-858494">
              Scan the station QR displayed at your collection station.
            </p>
            {!hasRead && <QrCameraReader onRead={read} />}
            {reading && <p role="status">Loading station...</p>}
            {error && (
              <p role="alert" className="text-sm text-fd380f">
                {error}
              </p>
            )}
            <details>
              <summary className="py-2 text-sm text-3461fd">
                Enter QR contents
              </summary>
              <label className="grid gap-2 text-sm">
                Station QR contents
                <textarea
                  value={manual}
                  onChange={(event) => setManual(event.target.value)}
                  maxLength={2048}
                  className="rounded-lg border p-2"
                />
              </label>
              <Button
                type="button"
                disabled={reading || !manual.trim()}
                onClick={() => void read(manual.trim())}
              >
                Read code
              </Button>
            </details>
            {hasRead && !reading && (
              <Button
                type="button"
                variant={ButtonVariant.FLAT}
                onClick={() => {
                  setHasRead(false);
                  setError("");
                }}
              >
                Scan again
              </Button>
            )}
          </section>
        </Modal>,
        document.querySelector("#root") || document.body
      )}
    </>
  );
}
